import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/data/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "glass py-3" : "py-5 bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-3 group">
          <img
            src={COMPANY.logo}
            alt="HAS UMC"
            className="h-16 w-16 md:h-20 md:w-20 object-contain rounded-sm ring-1 ring-[#D4AF37]/30 bg-black"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-[30px] md:text-[34px] text-white tracking-wide">HAS</span>
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]/80">Universal · Management · Consultancy</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-[13px] tracking-[0.18em] uppercase transition-colors ${
                  isActive ? "text-[#D4AF37]" : "text-white/75 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute left-4 right-4 -bottom-0.5 h-px transition-all ${
                      isActive ? "bg-[#D4AF37]" : "bg-transparent group-hover:bg-white/30"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
            data-testid="navbar-phone"
            className="hidden md:inline-flex items-center gap-2 text-[12px] tracking-wider text-white/70 hover:text-[#D4AF37] transition-colors whitespace-nowrap"
          >
            <Phone size={14} className="text-[#D4AF37]" />
            {COMPANY.phone}
          </a>
          <Link to="/contact" data-testid="navbar-cta" className="hidden md:inline-flex btn-gold text-[12px] tracking-[0.16em] uppercase px-5 py-3">
            Book Consultation
          </Link>
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen(v => !v)}
            className="lg:hidden p-2 rounded-sm border border-white/10 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${open ? "max-h-[640px] opacity-100" : "max-h-0 opacity-0"}`}
      >
        <div className="px-5 md:px-10 pt-4 pb-6 border-t border-white/5 bg-black/80 backdrop-blur-xl">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `block py-3 text-sm uppercase tracking-[0.22em] border-b border-white/5 ${
                  isActive ? "text-[#D4AF37]" : "text-white/80"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/contact" data-testid="mobile-navbar-cta" className="btn-gold mt-5 w-full">
            Book Consultation
          </Link>
        </div>
      </div>
    </header>
  );
}
